import { useState, useEffect } from 'react';

import moment from 'moment';
import { DateRange } from 'react-day-picker';

import { PageTitle } from '../components/page-title';
import { CreditUsage } from './components/credit-usage';
import { DatePresetOptions } from './components/date-preset-options';
import { SeatCreditUsage } from './components/seat-credit-usage';
import { SeatPeriodChart } from './components/seat-period-chart';
import { StatisticBlock } from './components/statistic-block';
import { UsageTable } from './components/usage-table';
import { blockedReasonMessage } from '../../../../backend/src/utils/billing-utils';
import { formatChartData } from '../../../../backend/src/utils/usageDataFormatting';
import useFetchOrganisations from '../../api/queries/analyticsQueries/useFetchOrganisations';
import useFetchOrgMatters from '../../api/queries/analyticsQueries/useFetchOrgMatters';
import useFetchOrgUsageData from '../../api/queries/analyticsQueries/useFetchOrgUsageData';
import useFetchOrgUsers from '../../api/queries/analyticsQueries/useFetchOrgUsers';
import { useFetchSeatBasedUsage } from '../../api/queries/analyticsQueries/useFetchSeatBasedUsage';
import { User } from '../../api/types/backend-dto-types';
import useGetFetchConfig from '../../api/useGetFetchConfig';
import { useUserContext } from '../../Contexts/User';
import { MatterType } from '../../types';
import { Breadcrumb } from '../components/breadcrumb';
import { Icon } from '../components/icon';
import { Alert, AlertDescription, AlertTitle } from '../components/shadcn/alert';
import { Button } from '../components/shadcn/button';
import { ButtonGroup } from '../components/shadcn/button-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/shadcn/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/shadcn/tooltip';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import downloadUsage from '@/api/queries/analyticsQueries/downloadUsage';
import { Matter } from '@/api/types/backend-dto-types';
import ChartCard from '@/new/components/chart-card';
import { AreaChart } from '@/new/components/charts/area-chart';
import { ClearableSelect } from '@/new/components/clearable-select';
import { DateRangePicker } from '@/new/components/date-range-picker';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';

export function Usage() {
  const [view, setView] = useState<'table' | 'chart'>('chart');
  // Default range is past month
  const [filterDateRange, setFilterDateRange] = useState<DateRange>({
    from: moment().subtract(1, 'months').toDate(),
    to: new Date(),
  });

  function setPresetRange(amount: moment.DurationInputArg1, unit: moment.DurationInputArg2) {
    setFilterDateRange({
      from: moment().subtract(amount, unit).toDate(),
      to: moment().toDate(),
    });
  }

  const { user } = useUserContext();
  if (!user) return null;

  const [selectedUserId, setSelectedUserId] = useState<string>();
  const [selectedMatterId, setSelectedMatterId] = useState<string>();
  const [selectedOrganisationId, setSelectedOrganisationId] = useState<string>('');
  const [isDownloading, setIsDownloading] = useState(false);

  const organisationId = selectedOrganisationId !== '' ? selectedOrganisationId : user.organisationId;

  const { fetchConfigGET } = useGetFetchConfig();

  const handleDownloadClick = async () => {
    setIsDownloading(true);
    try {
      await downloadUsage(
        filterDateRange.from ?? null,
        filterDateRange.to ?? null,
        fetchConfigGET,
        selectedMatterId,
        selectedUserId,
        organisationId,
      );
      trackCustomEvent(TrackingEventsV2.UsageDashboardDownloadData);
    } finally {
      setIsDownloading(false);
    }
  };

  // Fetch users and matters in the organization
  const { data: usersData, refetch: refetchUsers } = useFetchOrgUsers(organisationId);
  const { data: mattersData, refetch: refetchMatters } = useFetchOrgMatters(organisationId);
  // Fetch organisations for Wexler admins
  const { data: organisationsData } = useFetchOrganisations(user?.organisation?.isGlobalAdmin ?? false);

  const { data: seatBasedUsage, isLoading: isSeatLoading } = useFetchSeatBasedUsage();

  // Fetch all the usage data
  const {
    data: usageData,
    isPreviousData,
    isLoading: isUsageLoading,
  } = useFetchOrgUsageData(
    true,
    filterDateRange.from ? filterDateRange.from.toISOString() : '',
    filterDateRange.to ? filterDateRange.to.toISOString() : '',
    selectedMatterId,
    selectedUserId,
    organisationId,
  );

  const userOptions =
    (usersData &&
      usersData.length &&
      usersData.map((user: User) => ({
        label: user.email,
        value: user.id,
      }))) ||
    [];

  const isViewingOtherOrg = user?.organisation?.isGlobalAdmin && selectedOrganisationId !== '';

  const matterOptions =
    (mattersData &&
      mattersData.length &&
      mattersData
        .filter((matter: Matter) => !matter.isDraft)
        .map((matter) => {
          let label;

          if (isViewingOtherOrg) {
            label = matter.id;
          } else if (matter.type === MatterType.Personal) {
            label = `${matter.name} - ${matter.createdBy.email}`;
          } else {
            label = matter.name;
          }

          return {
            label,
            value: matter.id,
          };
        })) ||
    [];

  const organisationOptions =
    (organisationsData &&
      organisationsData.length &&
      organisationsData.map((organisation) => ({
        label: organisation.name,
        value: organisation.id,
      }))) ||
    [];

  useEffect(() => {
    refetchUsers();
    setSelectedUserId('');
  }, [organisationId]);
  useEffect(() => {
    refetchMatters();
    setSelectedMatterId('');
  }, [organisationId]);

  const stats = [
    { title: 'Pages processed', value: usageData?.totals.pages },
    { title: 'Runs submitted', value: usageData?.totals.runs },
    { title: 'Chronologies created', value: usageData?.totals.chronologies },
    { title: 'Facts extracted', value: usageData?.totals.facts },
    { title: 'Kim queries', value: usageData?.totals.threads },
    { title: 'Credits used', value: usageData?.totals.tokens },
  ];

  return (
    <>
      <Breadcrumb items={[{ href: '/usage', label: 'Usage', active: true }]} />
      <PageContainer>
        <PageHeader>
          <PageTitle text="Usage Dashboard" />

          <Button variant="primary" onClick={handleDownloadClick} disabled={isDownloading}>
            <Icon className={isDownloading ? 'lucide--loader-circle animate-spin duration-1500' : 'lucide--download'} />
            <span>Download Usage Data</span>
          </Button>
        </PageHeader>

        {user.isBlocked &&
          (() => {
            const { title, description } = blockedReasonMessage(user.blockedReason, user.blockedUntil);
            return (
              <div className="-mx-2">
                <Alert variant="destructive">
                  <div>
                    <AlertTitle>{title}</AlertTitle>
                    <AlertDescription>{description}</AlertDescription>
                  </div>
                </Alert>
              </div>
            );
          })()}

        <div className="-mx-2 mt-4 rounded-xl border bg-gray-50 p-7 py-6">
          {seatBasedUsage ? (
            <SeatCreditUsage data={seatBasedUsage} isLoading={isSeatLoading} />
          ) : (
            <CreditUsage periodCreditUsage={usageData?.periodCreditUsage ?? null} isLoading={isUsageLoading} />
          )}
        </div>

        {seatBasedUsage && seatBasedUsage.periods.length > 0 && <SeatPeriodChart data={seatBasedUsage} />}

        {/* Filters */}
        <div className="mt-8 flex items-center justify-between gap-4">
          <div className="ml-2">
            <div className="flex items-center gap-2">
              <h2 className="font-serif text-2xl font-medium">Usage Statistics</h2>
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <button type="button" className="shrink-0" aria-label="About usage statistics">
                      <Icon className="lucide--info h-4 w-4 text-gray-400" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-m">
                    Figures are updated daily and may not include recent activity.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <p className="mt-2 ml-0.5 text-gray-500">
              Between <span className="font-medium">{filterDateRange?.from?.toDateString()}</span> and{' '}
              <span className="font-medium">{filterDateRange?.to?.toDateString()}</span>
            </p>

            <ButtonGroup className="mt-4">
              <Button
                className={`py-1.5 ${view === 'chart' ? 'bg-gray-100 text-gray-900' : 'text-gray-600'}`}
                onClick={() => setView('chart')}
              >
                <span
                  className={`iconify lucide--line-chart ${view === 'chart' ? 'text-gray-700' : 'text-gray-500'}`}
                ></span>
                Charts
              </Button>

              <Button
                className={`py-1.5 ${view === 'table' ? 'bg-gray-100 text-gray-900' : 'text-gray-600'}`}
                onClick={() => setView('table')}
              >
                <span
                  className={`iconify lucide--table ${view === 'table' ? 'text-gray-700' : 'text-gray-500'}`}
                ></span>
                Table
              </Button>
            </ButtonGroup>
          </div>

          <div className="flex grow flex-col items-end justify-end gap-2">
            <DatePresetOptions setPresetRange={setPresetRange} />
            <DateRangePicker dateRange={filterDateRange} setDateRange={setFilterDateRange} />

            <div className="flex w-full justify-end gap-4">
              {user?.organisation?.isGlobalAdmin && (
                <div className="flex items-center justify-end gap-2">
                  <Icon className="lucide--building text-gray-600" />
                  <ClearableSelect onClear={() => setSelectedOrganisationId('')}>
                    <Select value={selectedOrganisationId} onValueChange={(v) => setSelectedOrganisationId(v)}>
                      <SelectTrigger className="w-56">
                        <SelectValue placeholder="Select an organisation" />
                      </SelectTrigger>
                      <SelectContent className="max-h-64">
                        {organisationOptions.map((organisation) => (
                          <SelectItem key={organisation.value} value={organisation.value}>
                            {organisation.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </ClearableSelect>
                </div>
              )}

              <div className="flex items-center justify-end gap-2">
                <Icon className="lucide--user-2 text-gray-600" />
                <ClearableSelect onClear={() => setSelectedUserId('')}>
                  <Select value={selectedUserId} onValueChange={(v) => setSelectedUserId(v)}>
                    <SelectTrigger className="w-52">
                      <SelectValue placeholder="Select a user" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {userOptions.map((user) => (
                        <SelectItem key={user.value} value={user.value}>
                          {user.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </ClearableSelect>
              </div>

              <div className="flex items-center gap-2">
                <Icon className="lucide--folder-open text-gray-600" />
                <ClearableSelect onClear={() => setSelectedMatterId('')}>
                  <Select value={selectedMatterId} onValueChange={(v) => setSelectedMatterId(v)}>
                    <SelectTrigger className="w-52">
                      <SelectValue placeholder="Select a matter" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {matterOptions.map((matter) => (
                        <SelectItem key={matter.value} value={matter.value}>
                          {matter.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </ClearableSelect>
              </div>
            </div>
          </div>
        </div>

        {/* Charts and headline figures */}
        {view === 'chart' && (
          <>
            {/* Headline figures */}
            <div
              className={`my-8 grid grid-cols-[repeat(auto-fit,_minmax(11rem,_auto))] flex-wrap gap-3 ${isPreviousData ? 'opacity-50' : ''} duration-150`}
            >
              {stats.map((stat) => (
                <StatisticBlock key={stat.title} title={stat.title} value={stat.value} />
              ))}
            </div>

            <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
              <ChartCard title="Pages processed" className="transition-shadow duration-200">
                {usageData?.data ? (
                  <AreaChart
                    className="h-60"
                    data={formatChartData(
                      usageData.data || [],
                      'pages',
                      filterDateRange.from ?? null,
                      filterDateRange.to ?? null,
                    )}
                    index="week"
                    categories={['pages']}
                    valueFormatter={(value) => (Number.isInteger(value) ? value.toString() : '')}
                  />
                ) : (
                  <div className="flex h-60 items-center justify-center p-8">
                    <span className="iconify lucide--loader-circle"></span>
                  </div>
                )}
              </ChartCard>
              <ChartCard title="Runs submitted" className="transition-shadow duration-200">
                {usageData?.data ? (
                  <AreaChart
                    className="h-60"
                    data={formatChartData(
                      usageData.data || [],
                      'runs',
                      filterDateRange.from ?? null,
                      filterDateRange.to ?? null,
                    )}
                    index="week"
                    categories={['runs']}
                    valueFormatter={(value) => (Number.isInteger(value) ? value.toString() : '')}
                  />
                ) : (
                  <div className="flex h-60 items-center justify-center p-8">
                    <span className="iconify lucide--loader-circle"></span>
                  </div>
                )}
              </ChartCard>
              <ChartCard title="Chronologies created" className="transition-shadow duration-200">
                {usageData?.data ? (
                  <AreaChart
                    className="h-60"
                    data={formatChartData(
                      usageData.data || [],
                      'chronologies',
                      filterDateRange.from ?? null,
                      filterDateRange.to ?? null,
                    )}
                    index="week"
                    categories={['chronologies']}
                    valueFormatter={(value) => (Number.isInteger(value) ? value.toString() : '')}
                  />
                ) : (
                  <div className="flex h-60 items-center justify-center p-8">
                    <span className="iconify lucide--loader-circle"></span>
                  </div>
                )}
              </ChartCard>
              <ChartCard title="Kim queries" className="transition-shadow duration-200">
                {usageData?.data ? (
                  <AreaChart
                    className="h-60"
                    data={formatChartData(
                      usageData.data || [],
                      'threads',
                      filterDateRange.from ?? null,
                      filterDateRange.to ?? null,
                    )}
                    index="week"
                    categories={['threads']}
                    valueFormatter={(value) => (Number.isInteger(value) ? value.toString() : '')}
                  />
                ) : (
                  <div className="flex h-60 items-center justify-center p-8">
                    <span className="iconify lucide--loader-circle"></span>
                  </div>
                )}
              </ChartCard>
              <ChartCard title="Credits used" className="col-span-2 transition-shadow duration-200">
                {usageData?.data ? (
                  <AreaChart
                    className="h-60"
                    data={formatChartData(
                      usageData.data || [],
                      'credits',
                      filterDateRange.from ?? null,
                      filterDateRange.to ?? null,
                    )}
                    index="week"
                    categories={['credits']}
                    valueFormatter={(value) => (Number.isInteger(value) ? value.toString() : '')}
                  />
                ) : (
                  <div className="flex h-60 items-center justify-center p-8">
                    <span className="iconify lucide--loader-circle"></span>
                  </div>
                )}
              </ChartCard>
            </div>
          </>
        )}

        {/* Table */}
        {view === 'table' && (
          <div className="mx-2 my-10 w-full">
            <UsageTable
              filterDateRange={filterDateRange}
              selectedMatterId={selectedMatterId}
              selectedUserId={selectedUserId}
              organisationId={organisationId}
              isViewingOtherOrg={isViewingOtherOrg}
            />
          </div>
        )}
      </PageContainer>
    </>
  );
}
